// Jalaali (Persian) calendar conversion — ported from jalaali-js (MIT,
// https://github.com/jalaali/jalaali-js), the same library the previous
// Emrouz vendored. Kept as a local port because the app has a
// no-runtime-dependencies rule (see AGENTS.md).

export interface IJalaaliDate {
    jy: number
    jm: number
    jd: number
}

export interface IGregorianDate {
    gy: number
    gm: number
    gd: number
}

export function toJalaali(gy: number, gm: number, gd: number): IJalaaliDate {
    return d2j(g2d(gy, gm, gd))
}

export function toGregorian(jy: number, jm: number, jd: number): IGregorianDate {
    return d2g(j2d(jy, jm, jd))
}

export function isLeapJalaaliYear(jy: number): boolean {
    return jalCal(jy).leap === 0
}

export function jalaaliMonthLength(jy: number, jm: number): number {
    if (jm <= 6) return 31
    if (jm <= 11) return 30
    return isLeapJalaaliYear(jy) ? 30 : 29
}

// Determines if the Jalaali year is leap, the Gregorian year of the
// corresponding Farvardin 1st, and the March day of Farvardin 1st.
function jalCal(jy: number) {
    // Jalaali years starting the 33-year rule.
    const breaks = [
        -61, 9, 38, 199, 426, 686, 756, 818, 1111, 1181,
        1210, 1635, 2060, 2097, 2192, 2262, 2324, 2394, 2456, 3178,
    ]
    const bl = breaks.length
    const gy = jy + 621
    let leapJ = -14
    let jp = breaks[0]

    if (jy < jp || jy >= breaks[bl - 1]) throw new Error('Invalid Jalaali year ' + jy)

    // Find the limiting years for the Jalaali year jy.
    let jump = 0
    for (let i = 1; i < bl; i += 1) {
        const jb = breaks[i]
        jump = jb - jp
        if (jy < jb) break
        leapJ = leapJ + div(jump, 33) * 8 + div(mod(jump, 33), 4)
        jp = jb
    }
    let n = jy - jp

    // Find the number of leap years from AD 621 to the beginning of the
    // current Jalaali year in the Persian calendar.
    leapJ = leapJ + div(n, 33) * 8 + div(mod(n, 33) + 3, 4)
    if (mod(jump, 33) === 4 && jump - n === 4) leapJ += 1

    // And the same in the Gregorian calendar (until the year gy).
    const leapG = div(gy, 4) - div((div(gy, 100) + 1) * 3, 4) - 150

    // Determine the Gregorian date of Farvardin the 1st.
    const march = 20 + leapJ - leapG

    // Find how many years have passed since the last leap year.
    if (jump - n < 6) n = n - jump + div(jump + 4, 33) * 33
    let leap = mod(mod(n + 1, 33) - 1, 4)
    if (leap === -1) leap = 4

    return { leap, gy, march }
}

// Julian Day number from a Jalaali date.
function j2d(jy: number, jm: number, jd: number) {
    const r = jalCal(jy)
    return g2d(r.gy, 3, r.march) + (jm - 1) * 31 - div(jm, 7) * (jm - 7) + jd - 1
}

// Jalaali date from a Julian Day number.
function d2j(jdn: number): IJalaaliDate {
    const gy = d2g(jdn).gy // calculate Gregorian year (gy)
    let jy = gy - 621
    const r = jalCal(jy)
    const jdn1f = g2d(gy, 3, r.march)

    // Find number of days that passed since 1 Farvardin.
    let k = jdn - jdn1f
    if (k >= 0) {
        if (k <= 185) {
            // The first 6 months.
            return { jy, jm: 1 + div(k, 31), jd: mod(k, 31) + 1 }
        }
        k -= 186
    } else {
        // Previous Jalaali year.
        jy -= 1
        k += 179
        if (r.leap === 1) k += 1
    }
    return { jy, jm: 7 + div(k, 30), jd: mod(k, 30) + 1 }
}

// Julian Day number from a Gregorian date (valid for the Gregorian proleptic
// calendar, gy > 0).
function g2d(gy: number, gm: number, gd: number) {
    let d = div((gy + div(gm - 8, 6) + 100100) * 1461, 4)
        + div(153 * mod(gm + 9, 12) + 2, 5)
        + gd - 34840408
    d = d - div(div(gy + 100100 + div(gm - 8, 6), 100) * 3, 4) + 752
    return d
}

// Gregorian date from a Julian Day number.
function d2g(jdn: number): IGregorianDate {
    let j = 4 * jdn + 139361631
    j = j + div(div(4 * jdn + 183187720, 146097) * 3, 4) * 4 - 3908
    const i = div(mod(j, 1461), 4) * 5 + 308
    const gd = div(mod(i, 153), 5) + 1
    const gm = mod(div(i, 153), 12) + 1
    const gy = div(j, 1461) - 100100 + div(8 - gm, 6)
    return { gy, gm, gd }
}

function div(a: number, b: number) {
    return ~~(a / b)
}

function mod(a: number, b: number) {
    return a - ~~(a / b) * b
}
